import { watchThrottled } from '@vueuse/core';
import {
  computed,
  isRef,
  onUnmounted,
  ref,
  unref,
  watch,
  type MaybeRef,
} from 'vue';
import type {
  FormFieldValidatatorRule,
  FormFieldValidationRuleSet,
} from '~/composables/useFormValidation/types';

export default <T extends object | Record<string, unknown>>(
  formData: T,
  rules: MaybeRef<FormFieldValidationRuleSet<T>>,
) => {
  const invalidFields = ref<Partial<Record<keyof T, string>>>({});

  const userChangedFields = ref<Array<string>>(
    Object.entries(formData)
      .map(([key, value]) => {
        if (typeof value === 'string') {
          return value != null && value.length > 0 ? key : undefined;
        }

        if (typeof value === 'boolean') {
          return key;
        }

        return undefined;
      })
      .filter((field) => field != null),
  );
  const isFormValid = computed(() => {
    const validationRules = unref(rules);
    // from every field with a rule, we have to check whether all required fields are touched at least once
    const isAllFieldsChangedOnce = Object.keys(validationRules).every(
      (field) => {
        // if the rule is always valid, assume that the field is changed, regarding of the input of the user
        if (
          validationRules[field] != null &&
          (validationRules[field] as FormFieldValidatatorRule).name ===
            'always-valid'
        ) {
          return true;
        }

        return Array.from(userChangedFields.value).some(
          (changedField) => changedField === field,
        );
      },
    );

    return (
      isAllFieldsChangedOnce && Object.values(invalidFields.value).length === 0
    );
  });

  const setInvalidField = (
    fieldName: string,
    invalidatedRule: FormFieldValidatatorRule<T>,
  ) => {
    invalidFields.value = Object.keys(invalidFields.value).reduce(
      (acc, key) => {
        if (key !== fieldName) {
          acc[key] = invalidFields.value[key];
        }
        return acc;
      },
      {} as typeof invalidFields.value,
    );

    invalidFields.value[fieldName] = invalidatedRule.errorI18nKey;
  };

  const clearInvalidField = (fieldName: string) => {
    delete invalidFields.value[fieldName];
    invalidFields.value = Object.keys(invalidFields.value).reduce(
      (acc, key) => {
        if (key !== fieldName) {
          acc[key] = invalidFields.value[key];
        }
        return acc;
      },
      {} as typeof invalidFields.value,
    );
  };

  // Re-run against the current formData value and whatever rule the field has *right now* -
  // shared by the per-field value watcher and the rule-set watcher below, so a field is
  // validated the same way whether it was the value or the rule that just changed.
  const validateField = (field: string) => {
    const rule = unref(rules)[field];
    const newValue = formData[field];

    if (rule != null) {
      if ((rule as FormFieldValidatatorRule).test != null) {
        // rule set is a rule
        const invalidatedRule = rule as FormFieldValidatatorRule;

        if (!invalidatedRule.test(newValue, formData)) {
          setInvalidField(field, invalidatedRule);
        } else {
          clearInvalidField(field);
        }
      } else if ((rule as Array<FormFieldValidatatorRule>).length) {
        // rule set is an array
        const fieldRules = rule as Array<FormFieldValidatatorRule>;
        const firstInvalidatedRule = fieldRules.find(
          (ruleFromSet) => !ruleFromSet.test(newValue, formData),
        );

        if (firstInvalidatedRule != null) {
          setInvalidField(field, firstInvalidatedRule);
        } else {
          clearInvalidField(field);
        }
      }
    } else if (invalidFields[field] != null) {
      // restore the functionality, if a rule cannot be found
      clearInvalidField(field);
    }
  };

  const fieldValueWatches = Object.keys(formData).map((field) =>
    watchThrottled(
      () => formData[field],
      () => {
        // add to changed fields
        if (
          !userChangedFields.value.some(
            (changedField) => changedField === field,
          )
        ) {
          userChangedFields.value.push(field);
        }

        validateField(field);
      },
    ),
  );

  // A field's own value watcher above only fires when that field's value changes. When the
  // rule *set* changes underneath an unchanged value - e.g. a country switch that makes a
  // postal code optional or drops it entirely - nothing re-runs, so a stale invalidFields entry
  // from before the switch survives and can lock isFormValid with no visible error left to
  // explain why (TV-1970 review). Re-validate every field the customer has already touched, so
  // a field they haven't reached yet is not flagged before they get to it.
  let ruleSetWatch: (() => void) | undefined;
  if (isRef(rules)) {
    ruleSetWatch = watch(rules, () => {
      userChangedFields.value.forEach((field) => validateField(field));
    });
  }

  // here we want to implement handler for cross references
  const referenceWatches = [];
  // first look for all rules with a cross reference
  Object.keys(unref(rules)).forEach((originalField) => {
    const rule = unref(rules)[originalField];
    let fieldRule = rule as Array<FormFieldValidatatorRule>;
    if ((rule as Array<FormFieldValidatatorRule>).length == null) {
      fieldRule = [rule];
    }

    // look into rule/s for each field
    fieldRule.forEach((singleRule) => {
      // ignore rules without the references option
      if (singleRule.references == null) return;

      // now look into the referenced fields
      let referencedFields = singleRule.references as Array<keyof T>;
      // if it is a single string
      if (typeof (singleRule.references as Array<keyof T>) !== 'object') {
        referencedFields = [singleRule.references as unknown as keyof T];
      }

      // now go through all referenced fields in references
      referencedFields.forEach((referencedField) => {
        // create a new watcher for the field (a field can have multiple watchers)
        const watchHandler = watchThrottled(
          () => formData[referencedField],
          () => {
            // add to changed fields when field is edited
            if (
              !userChangedFields.value.some(
                (changedField) => changedField === (referencedField as string),
              )
            ) {
              userChangedFields.value.push(referencedField as string);
            }

            // validation with the original rule
            const isValid = singleRule.test(formData[originalField], formData);
            // we only want to show an error message, if original field was edited before
            const oringalFieldWasEdited = userChangedFields.value.some(
              (field) => field === originalField,
            );

            // now either set or clear the field error
            if (!isValid && oringalFieldWasEdited) {
              setInvalidField(originalField, singleRule);
            } else {
              clearInvalidField(originalField);
            }
          },
        );

        referenceWatches.push(watchHandler);
      });
    });
  });

  onUnmounted(() => {
    fieldValueWatches.forEach((handler) => handler());
    referenceWatches.forEach((handler) => handler());
    ruleSetWatch?.();
  });

  return {
    invalidFields,
    isFormValid,
    userChangedFields,
  };
};
